import { OperationTimeoutError } from './operation-timeout-error.js';

/**
 * Race an operation against a driver-side timeout.
 *
 * The wrapped operation may ignore cancellation, so this helper only bounds the
 * caller's wait. The underlying promise can still settle later; callers that
 * need resource teardown must do that separately.
 */
export async function withOperationTimeout<T>(
  operation: Promise<T>,
  timeoutMs: number,
  label: string
): Promise<T> {
  // Clear the timer when the operation wins, otherwise it lingers in Node's
  // timer queue until it fires (e.g. a 20s GET_TITLE timer on every snapshot).
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new OperationTimeoutError(label, timeoutMs)), timeoutMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Bound best-effort metadata without hiding real browser/transport failures.
 * Only this helper's own driver-side timeout earns the fallback value.
 */
export async function withOperationTimeoutFallback<T>(
  operation: Promise<T>,
  timeoutMs: number,
  label: string,
  fallback: T
): Promise<T> {
  try {
    return await withOperationTimeout(operation, timeoutMs, label);
  } catch (error) {
    if (error instanceof OperationTimeoutError) return fallback;
    throw error;
  }
}
